// import type { Appointment, Coding, Slot } from 'fhir/r4'
import type { Appointment, CodeableConcept, Extension, Reference } from "fhir/r3"
import timestampFormatter from "./utils/timestampFormatter"
import { startOfDay } from "date-fns"
import { addDays } from "date-fns"
class SimplifiedAppointment {
  id: string
  appointmentId?: string
  priority?: string
  requestStatus?: string
  consultationMedium?: string
  status: string
  appointmentType: string
  start?: Date | string
  appointmentDate?: string
  appointmentTime?: string
  specialty: string[]
  serviceType: string[]
  referrer?: string
  participantPatientReference: Reference[]
  participantPatient: string[]
  participantPractitioner: string[]
  participantLocation: string[]
  participantLocationReference: Reference[]
  healthcareService?: string
  canBeCancelled: boolean
  canBeRescheduled: boolean
  locationId?: string
  deliveryChannel: "telephone" | "video" | "in-person"

  constructor(fhirAppointment: Appointment) {
    this.id = fhirAppointment.id ?? ""

    this.appointmentId = (fhirAppointment.identifier ?? [])[0]?.value
    this.status = fhirAppointment.status
    this.requestStatus = displayFromCode(findExtension(fhirAppointment, "ExtensionAppointmentStatus")?.valueCode)
    const deliveryChannel = findExtension(
      fhirAppointment,
      "Extension-CareConnect-DeliveryChannel-1",
    )?.valueCode?.toLowerCase()
    this.deliveryChannel =
      deliveryChannel === "telephone" ? "telephone" : deliveryChannel === "video" ? "video" : "in-person"

    this.appointmentType =
      fhirAppointment.description ??
      `${(fhirAppointment.appointmentType?.coding ?? [])[0]?.display ?? ""} ${(fhirAppointment.reason ?? [])[0]?.text ?? ""}`.trim()

    this.participantPatientReference = []
    this.participantPatient = []
    this.participantPractitioner = []
    this.participantLocation = []
    this.participantLocationReference = []

    this.priority = displayFromCode(findExtension(fhirAppointment, "Priority")?.valueCode)
    this.consultationMedium = displayFromCode(
      findExtension(fhirAppointment, "Extension-Consultation-Medium")?.valueCode,
    )
    if (fhirAppointment.start != null) {
      const startDate = new Date(fhirAppointment.start)
      const appointmentStartDisplay = timestampFormatter(startDate)

      this.start = startDate

      this.appointmentDate = appointmentStartDisplay.displayDate
      this.appointmentTime = appointmentStartDisplay.displayTime
    }
    this.specialty = displayValuesFromCodeableConcepts(fhirAppointment.specialty ?? [])

    this.serviceType = displayValuesFromCodeableConcepts(fhirAppointment.serviceType ?? [])
    fhirAppointment.participant.forEach((participant) => {
      const actor = participant.actor ?? {}

      const participantType = (participant.type ?? [])
        .map((t) => {
          const coding = t.coding?.find(
            (c) =>
              c.system === "https://fhir.yhcr.nhs.uk/ValueSet/Interweave-AppointmentParticipationType-1" ||
              "https://fhir.yhcr.nhs.uk/ValueSet/Interweave-AppointmentParticipationHealthcareServiceType-1",
          )
          return coding?.code
        })
        .filter((type) => type != null)

      if (participantType.length > 0) {
        if (participantType.includes("SBJ")) {
          this.participantPatientReference.push(actor)
          this.participantPatient.push(actor.display ?? "")
        } else if (participantType.includes("ATND") && actor.display) {
          this.participantPractitioner.push(actor.display)
        } else if (participantType.includes("LOC")) {
          this.participantLocationReference.push(actor)
          this.participantLocation.push(actor.display ?? "")
        } else if (participantType.includes("REF")) {
          this.referrer = actor.display ?? ""
        } else if (participantType.includes("HCP")) {
          this.healthcareService = actor.reference?.split("/")[1] ?? ""
        }
      } else {
        const type = actor.reference ?? ""
        if (type.includes("Patient")) {
          this.participantPatientReference.push(actor)
          this.participantPatient.push(actor.display ?? "")
        } else if (type.includes("Practitioner") && actor.display) {
          this.participantPractitioner.push(actor.display)
        } else if (type.includes("Location")) {
          this.participantLocationReference.push(actor)
          this.participantLocation.push(actor.display ?? "")
        } else if (type.includes("Organization")) {
          this.referrer = actor.display ?? ""
        } else if (type.includes("HCP")) {
          this.healthcareService = actor.reference?.split("/")[1] ?? ""
        }
      }
    })
    const isFuture = this.start ? this.start > startOfDay(addDays(Date.now(), 1)) : false

    this.canBeCancelled = this.status === "booked" && isFuture && !(this.requestStatus?.includes("pending") ?? false)
    this.canBeRescheduled = this.status === "booked" && isFuture && !(this.requestStatus?.includes("pending") ?? false)

    this.locationId =
      this.participantLocationReference?.length && this.participantLocationReference?.length > 0
        ? this.participantLocationReference[0]?.reference?.split("Location/")[1]
        : undefined
  }
}

function displayValuesFromCodeableConcepts(codings?: CodeableConcept[]): string[] {
  if (codings == null) return []

  return codings.flatMap((c) => c.coding ?? []).map((coding) => (coding.display ?? "").toLowerCase()) ?? []
}
function findExtension(fhirAppointment: Appointment, extensionName: string): Extension | undefined {
  if (fhirAppointment.extension != null) {
    return fhirAppointment.extension.find((extension) => extension.url.includes(extensionName))
  }
}
function displayFromCode(code?: string): string | undefined {
  if (code != null) {
    return code.replaceAll("_", " ").toLowerCase()
  }
}

export { SimplifiedAppointment }
